// ** React Imports
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Styles from './convert.module.css';
import { useDispatch, useSelector } from 'react-redux';

// ** Icons Imports
import { ReactComponent as SteadyValueIcon } from '../assets/steady_value.svg';
import { ReactComponent as CountlessPairsIcon } from '../assets/countless_pairs.svg';
import { ReactComponent as InstantConvertIcon } from '../assets/instant_convert.svg';
import { ArrowUpDown, Info, AlertCircle } from 'lucide-react';
import { FormattedMessage, injectIntl } from 'react-intl';

// ** Redux Imports
import { _getBalances } from '../../exchange/redux/actions';
import { setBalances } from '../../../redux/slices/balancesSlice';
import { setConfirmConvertModalData } from '../../modals/convert/redux/confirmConvertModalSlice';
import { estimateUnitValue } from '../../helpers/estimateValueBase';
import {
  _getAvailableCurrencies,
  _getCoinKline,
  _getCurrencies,
  _previewConversion,
  _getMaximumConvertAmount
} from '../redux/actions';

import { showErrorSnackbar } from '../../snackbar/helpers';

import ConvertFeature from '../Components/ConvertFeature';
import Accordions from '../../../components/Accordions/Accordions';
import ConfirmConvertModal from '../../modals/convert/ConfirmConvertModal';
import SuccessConvertModal from '../../modals/convert/SuccessConvertModal';
import SectionHeader from '../../../components/SectionHeader/SectionHeader';
import ExchangeRate from '../../../components/ExchangeRate';
import AreaChart from '../../../components/AreaChart/AreaChart';
import { _getFAQCategories, _getFAQQuestions } from '../../FAQs/redux/actions';
import { setFAQCategories } from '../../FAQs/redux/slices/FAQCategoriesSlice';
import { setFAQQuestions } from '../../FAQs/redux/slices/FAQQuestionsSlice';
import {
  setSelectCurrencyModalData,
  setSelectCurrencyModalOpen
} from '../../modals/select-currency/redux/selectCurrencyModalSlice';
import Alert from '../../../components/Alert/Alert';

import illustration from '../assets/illustration.svg';

function Convert() {
  // ** Content
  const convertFeaturesContent = [
    {
      icon: <InstantConvertIcon />,
      title: 'Instant Convert'
    },
    {
      icon: <CountlessPairsIcon />,
      title: 'Countless Pairs'
    },
    {
      icon: <SteadyValueIcon />,
      title: 'Steady Value'
    }
  ]

  // ** Hooks
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { marketId } = useParams()
  const exchangeName = useSelector(state => state.exchangeCustomization.value.name)
  document.title = `${exchangeName}: Convert`

  // ** Store States
  const theme = useSelector(state => state.theme.value)
  const markets = useSelector(state => state.marketList.value)
  const isLoggedIn = useSelector(state => state.loggedIn.value)
  const selectedMarket = useSelector(state => state.selectedMarket.value)

  // ** States
  const [fromCurrency, setFromCurrency] = useState(
    marketId
      ? marketId.split('_')[0]?.toLowerCase()
      : markets.find(el => el.id === marketId)?.base_unit && markets.find(el => el.id === selectedMarket.id)?.quote_unit
  )
  const [fromValue, setFromValue] = useState('')
  const [fromKlineArray, setFromKlineArray] = useState([])
  const [questions, setQuestions] = useState([])

  const [toCurrency, setToCurrency] = useState(
    marketId
      ? marketId.split('_')[1]?.toLowerCase()
      : markets.find(el => el.id === marketId)?.quote_unit && markets.find(el => el.id === selectedMarket.id)?.base_unit
  )
  const [toValue, setToValue] = useState('')
  const [toKlineArray, setToKlineArray] = useState([])
  const [currencies, setCurrencies] = useState('')
  const marketTickers = useSelector(state => state.marketTickers.value)
  const balances = useSelector(state => state.balances.value)
  const userData = useSelector(state => state.userData?.value)
  const tickers = useSelector(state => state.marketTickers.value)
  const categories = useSelector(state => state.FAQCategories.value)

  const [calculatedBaseCurrency, setCalculatedBaseCurrency] = useState(0)
  const [calculatedQuoteCurrency, setCalculatedQuoteCurrency] = useState(0)
  const [calculatedBasePercentageChange, setCalculatedBasePercentageChange] = useState(0)
  const [calculatedQuotePercentageChange, setCalculatedQuotePercentageChange] = useState(0)
  const [confirmConvertModalOpen, setConfirmConvertModalOpen] = useState(false)
  const [successConvertModalOpen, setSuccessConvertModalOpen] = useState(
    useSelector(state => state.successConvertModalState.open)
  )

  // First add a state for available currencies
  const [availableCurrencies, setAvailableCurrencies] = useState([])

  // Add states for maximum conversion amount data
  const [fromMaxAmount, setFromMaxAmount] = useState(null)
  const [fromConversionFees, setFromConversionFees] = useState(null)
  const [toMaxAmount, setToMaxAmount] = useState(null)
  const [toConversionFees, setToConversionFees] = useState(null)

  const handleReverse = () => {
    // Fetch available currencies for the current toCurrency
    _getAvailableCurrencies(
      toCurrency,
      data => {
        // If fromCurrency is in the available currencies for toCurrency
        if (data.some(c => c.id === fromCurrency)) {
          // Safe to reverse
          const tempCurrency = fromCurrency
          setFromCurrency(toCurrency)
          setToCurrency(tempCurrency)
          setFromValue(toValue || convertedPrice)
          setToValue(fromValue)
        } else {
          // Show error that this pair cannot be reversed
          showErrorSnackbar({
            alertMessage: `Cannot convert from ${toCurrency.toUpperCase()} to ${fromCurrency.toUpperCase()}.`
          })
        }
      },
      err => {
        console.log('Error checking reverse conversion:', err)
        showErrorSnackbar({
          alertMessage: 'Unable to verify conversion pair'
        })
      }
    )
  }

  const handleFromValueChange = event => {
    setFromValue(event.target.value)
    setToValue('')
  }

  const handleToValueChange = event => {
    setToValue(event.target.value)
    setFromValue('')
  }

  const isAmountExceedingMax = useMemo(
    () =>
      parseFloat(
        fromMaxAmount?.maximum_amount || fromMaxAmount?.max_amount || fromMaxAmount?.maxAmount || fromMaxAmount?.amount
      ) < parseFloat(fromValue),
    [fromMaxAmount, fromValue]
  )

  const handleConvert = () => {
    const fromCurr = currencies.length && currencies?.find(el => el.id === fromCurrency)
    const toCurr = currencies.length && currencies?.find(el => el.id === toCurrency)
    if (fromValue) {
      _previewConversion(
        {
          from: fromCurr.id,
          to: toCurr.id,
          volume: fromValue
        },
        data => {
          dispatch(setConfirmConvertModalData(data))
          dispatch(setConfirmConvertModalOpen(true))
        },
        error => {
          // handleClose()
          showErrorSnackbar({
            alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error'
          })
        }
      )
    } else {
      showErrorSnackbar({ alertMessage: 'You should insert values first' })
      // dispatch(setSnackbarOpen(true))
      // dispatch(
      //   setSnackbarData({
      //     alertMessage: "You should insert values first",
      //     severity: "error",
      //     //buttonMessage: 'See order',
      //     callback: () => {},
      //     //actionType: 'link', // link - close
      //     //linkTo: '/account/orders/open',
      //     position: "topRight"
      //   })
      // )
    }
  }

  // useEffect(() => {
  //   convertedPrice = estimateUnitValue(
  //     fromValue ? toCurrency : fromCurrency,
  //     fromValue ? fromCurrency : toCurrency,
  //     parseFloat(fromValue || toValue),
  //     currencies,
  //     markets,
  //     marketTickers
  //   )
  //   if (!fromValue) {
  //     setFromValue(convertedPrice)
  //   } else if (!toValue) {
  //     setToValue(convertedPrice)
  //   }
  // }, [fromValue])

  // get the kline data of coixns to convert to display in ApexChart
  useEffect(() => {
    _getCoinKline(
      {
        pairs: [`${fromCurrency}usd`, `${toCurrency}usd`],
        limit: 15
      },
      data => {
        const fromKline = data.find(el => Object.keys(el)[0] === `${fromCurrency}usd`)
        const toKline = data.find(el => Object.keys(el)[0] === `${toCurrency}usd`)

        setFromKlineArray(fromKline ? fromKline[Object.keys(fromKline)[0]] : [])
        setToKlineArray(toKline ? toKline[Object.keys(toKline)[0]] : [])
      },
      err => {
        console.log(err)
      }
    )
  }, [fromCurrency, toCurrency])

  const defaultTicker = {
    last: 0,
    vol: 0,
    price_change_percent: '+0.00%'
  }

  useEffect(() => {
    const fromMarket = markets.find(el => el.id === `${fromCurrency}${'usd'}`)
    const toMarket = markets.find(el => el.id === `${toCurrency}${'usd'}`)

    setCalculatedBaseCurrency((tickers[fromMarket?.id] || defaultTicker).last)
    setCalculatedQuoteCurrency((tickers[toMarket?.id] || defaultTicker).last)
    setCalculatedBasePercentageChange((tickers[fromMarket?.id] || defaultTicker).price_change_percent)
    setCalculatedQuotePercentageChange((tickers[toMarket?.id] || defaultTicker).price_change_percent)
  }, [tickers])

  // if there was no market pair in url or invalid coin names
  useEffect(() => {
    // if no currency was defined
    if (
      marketId === undefined ||
      marketId === null ||
      marketId === '' ||
      !marketId.includes('_') ||
      marketId.split('_').length > 2
    ) {
      navigate('/convert/BTC_USD')
      setFromCurrency('btc')
      setToCurrency('usd')
      // return
    }

    // if (marketId.includes("_")) {
    //   let id1 = ""
    //   let id2 = ""
    //   // check if id in URL is valid
    //   console.log(marketId.split("_")[0])

    //   checkCoinID(marketId.split("_")[0]).then((isValid) => {
    //     console.log('1', markets)
    //     if (isValid) {
    //       id1 = markets[0]?.base_unit
    //     } else {
    //       id1 = "BTC"
    //     }
    //     checkCoinID(marketId.split("_")[1]).then((isValid) => {
    //       console.log('2', markets[1])
    //       if (isValid) {
    //       id2 = markets[1]?.quote_unit
    //       } else {
    //         id2 = "USDT"
    //       }
    //       history.replace(`/convert/${id1.toUpperCase()}_${id2.toUpperCase()}`)
    //       setFromCurrency("btc")
    //       setToCurrency("usdt")
    //     })
    //   })
    // }
  }, [])

  const redirectToConvertHistory = id => {
    navigate(`/account/convert/history`)
  }

  // Hide body overflow-x
  useEffect(() => {
    document.getElementsByTagName('body')[0].style.overflowX = 'hidden'

    return () => {
      document.getElementsByTagName('body')[0].style.overflowX = 'auto'
    }
  }, [])

  // Then create the fixed fetchAvailableCurrencies helper function
  const fetchAvailableCurrencies = currency => {
    if (!currency) return

    console.log('Fetching available currencies for currency:', currency.toUpperCase())

    _getAvailableCurrencies(
      currency,
      data => {
        console.log(
          `Currencies that ${currency.toUpperCase()} can be converted TO:`,
          data.map(c => c.id.toUpperCase()).join(', ')
        )
        // These are currencies that the given currency can be converted TO
        setAvailableCurrencies(data)
      },
      err => {
        console.log('Error fetching available currencies:', err)
        setAvailableCurrencies([])
      }
    )
  }

  // Helper function to format numbers nicely - trim trailing zeros for better display
  const formatNumber = num => {
    if (!num) return '0.00'
    const parsed = parseFloat(num)
    const formatted = parsed.toFixed(8)
    return formatted.replace(/\.?0+$/, '');
  }

  // Helper function to fetch maximum conversion amount
  const fetchMaximumConversionAmount = (currency, setMaxAmount, setFees) => {
    // Check if user is logged in
    if (!isLoggedIn) {
      console.log('Cannot fetch maximum amount: User not logged in')
      return
    }

    // Check if currency is valid
    if (!currency) {
      console.log('Cannot fetch maximum amount: No currency specified')
      return
    }

    // Check if we have balances data
    if (!balances || !Array.isArray(balances)) {
      console.log('Cannot fetch maximum amount: No balances data available')
      return
    }

    // Check if user has balance for this currency
    const currencyBalance = balances.find(el => el.currency === currency)
    if (!currencyBalance || parseFloat(currencyBalance.balance || 0) <= 0) {
      console.log(`Cannot fetch maximum amount: No balance for ${currency}`)
      return
    }

    console.log(`Fetching maximum amount for ${currency}. Current balance: ${currencyBalance.balance}`)

    _getMaximumConvertAmount(
      currency,
      data => {
        console.log(`Maximum amount data received for ${currency}:`, data)
        setMaxAmount(data)
        setFees(data)
      },
      err => {
        console.error(`Error fetching maximum conversion amount for ${currency}:`, err)
        setMaxAmount(null)
        setFees(null)
      }
    )
  }

  const calculateFee = (amount, feesData) => {
    const numAmount = parseFloat(amount) || 0
    const feePercentage = parseFloat(feesData?.fees_percentage || 0)
    return ((numAmount * feePercentage) / 100).toFixed(6)
  }

  // Helper function to get currency icon
  const getCurrencyIcon = currency => {
    const iconStyle = {
      backgroundColor:
        currency === 'btc' ? '#f7931a' :
          currency === 'eth' ? '#627eea' :
            currency === 'usd' ? '#00d4aa' :
              currency === 'gbp' ? '#012169' :
                currency === 'eur' ? '#003399' :
                  '#6366f1'
    }

    const iconSymbol =
      currency === 'btc'
        ? '₿'
        : currency === 'eth'
          ? 'Ξ'
          : currency === 'usd'
            ? '$'
            : currency === 'gbp'
              ? '£'
              : currency === 'eur'
                ? '€'
                : currency?.toUpperCase()?.charAt(0)

    return { iconStyle, iconSymbol }
  }

  // Add a useEffect to fetch available currencies when component initially loads
  useEffect(() => {
    if (fromCurrency) {
      fetchAvailableCurrencies(fromCurrency)
    }
  }, [fromCurrency])

  useEffect(() => {
    // Fetch FAQ categories
    _getFAQCategories(
      data => {
        dispatch(setFAQCategories(data))
      },
      err => {
        console.log(err)
      }
    )
  }, [dispatch]) // Only dispatch dependency needed here

  useEffect(() => {
    // Fetch FAQ questions
    _getFAQQuestions(
      data => {
        dispatch(setFAQQuestions(data))
        // Map category_id to category names and filter by "Buy Crypto"
        const filteredQuestions = data
          .map(question => ({
            ...question,
            category: categories.find(category => category.id === question.category_id)
          }))
          .filter(question => question.category.name === 'Convert')

        setQuestions(filteredQuestions)
      },
      err => {
        console.log(err)
      }
    )
  }, [dispatch, categories])

  useEffect(() => {
    _getCurrencies(
      data => {
        setCurrencies(data)
      },
      err => {
        // console.log(err)
      }
    )
  }, [])

  useEffect(() => {
    // reset input values on currency change
    // setFromValue("")
    // setToValue("")

    if (isLoggedIn) {
      _getBalances(
        res => {
          dispatch(setBalances(res))
        },
        () => { }
      )
    }
  }, [fromCurrency, toCurrency])

  let convertedPrice = estimateUnitValue(
    fromValue ? toCurrency : fromCurrency,
    fromValue ? fromCurrency : toCurrency,
    parseFloat(fromValue || toValue),
    currencies,
    markets,
    marketTickers
  )

  useEffect(() => {
    convertedPrice = estimateUnitValue(
      toValue ? fromCurrency : toCurrency,
      toValue ? toCurrency : fromCurrency,
      parseFloat(toValue || fromValue),
      currencies,
      markets,
      marketTickers
    )

    // if (!fromValue) {
    //   setFromValue(convertedPrice)
    // } else if (!toValue) {
    //   setToValue(convertedPrice)
    // }
  }, [fromValue, toValue, fromCurrency, toCurrency])

  // Add useEffects to fetch maximum conversion amounts
  useEffect(() => {
    console.log('TESTTTTTTTTTTTTTTT')
    console.log(fromCurrency)
    console.log(isLoggedIn)
    if (fromCurrency && isLoggedIn) {
      console.log(`Fetching maximum conversion amount for FROM currency: ${fromCurrency}`)
      fetchMaximumConversionAmount(fromCurrency, setFromMaxAmount, setFromConversionFees)
    }
  }, [fromCurrency, isLoggedIn, balances])

  useEffect(() => {
    if (toCurrency && isLoggedIn) {
      console.log(`Fetching maximum conversion amount for TO currency: ${toCurrency}`)
      fetchMaximumConversionAmount(toCurrency, setToMaxAmount, setToConversionFees)
    }
  }, [toCurrency, isLoggedIn, balances])

  // Helper function to calculate fee

  return (
    <div className={Styles.pageContainer}>
      {/* Convert Background Illustration */}
      <div className={Styles.illustrationContainer}>
        <img
          src={illustration}
          style={{ filter: `opacity(${theme === 'dark' ? '0.275' : '0.1'})` }}
          className={Styles.convertIlustrationContainer}
        />
      </div>

      <div className={Styles.newConversionContainer}>
        {/* Header */}
        <div className={Styles.header}>
          <h1 className={Styles.title}>WealthTech Convert</h1>
          <button onClick={redirectToConvertHistory} className={Styles.historyButton}>
            Conversion History ↗
          </button>
        </div>

        {/* From Section */}
        {isAmountExceedingMax && (
          <Alert
            alertText='From Amount exceeds maximum conversion amount'
            textStyle={{ padding: '5px', fontSize: '0.875rem' }}
            cardColor='#b91c1c'
            textColor='#f87171'
            icon={<AlertCircle className={Styles.alertIcon} />}
            containerStyle={{ width: '100%' }}
          />
        )}
        <div className={Styles.inputSection}>
          <div className={Styles.sectionHeader}>
            <span className={Styles.sectionLabel}>From</span>
          </div>
          <div className={Styles.inputRow}>
            <input
              type='number'
              value={fromValue || convertedPrice ? fromValue || convertedPrice : ''}
              onChange={handleFromValueChange}
              className={Styles.amountInput}
              placeholder='0.00'
            />
            <div className={Styles.currencySelector}>
              <div className={Styles.currencyIcon} style={getCurrencyIcon(fromCurrency).iconStyle}>
                {/* <span>{getCurrencyIcon(fromCurrency).iconSymbol}</span> */}
                <img src={currencies ? currencies?.find(el => el.id === fromCurrency?.toLowerCase())?.icon_url : ''} />
              </div>
              <span className={Styles.currencyText}>{fromCurrency?.toUpperCase()}</span>
              <button
                onClick={() => {
                  if (toCurrency) {
                    // If "To" currency is already selected, fetch currencies that can convert TO that currency
                    _getAvailableCurrencies(
                      toCurrency,
                      availableCurrencies => {
                        // Log the available currencies
                        console.log(
                          `For TO=${toCurrency}, can convert FROM:`,
                          availableCurrencies.map(c => c.id.toUpperCase()).join(', ')
                        )

                        // Update the modal with available currencies
                        dispatch(
                          setSelectCurrencyModalData({
                            title: 'Select Currency',
                            searchPlaceholder: 'Search coin name',
                            showTabs: false,
                            type: 'coin',
                            currenciesToHide: [toCurrency],
                            availableCurrencies,
                            callback: coin => {
                              setFromCurrency(coin.id)
                              setFromValue('')
                              // Also fetch available currencies for new From currency
                              fetchAvailableCurrencies(coin.id)
                              navigate(`/convert/${coin.id?.toUpperCase()}_${toCurrency.toUpperCase()}`)
                            }
                          })
                        )

                        // Open the modal
                        dispatch(setSelectCurrencyModalOpen(true))
                      },
                      err => {
                        console.error('Error fetching available currencies:', err)
                        showErrorSnackbar({
                          alertMessage: 'Error loading available currencies'
                        })
                      }
                    )
                  } else {
                    // If To currency not selected, show all currencies
                    dispatch(
                      setSelectCurrencyModalData({
                        title: 'Select Currency',
                        searchPlaceholder: 'Search coin name',
                        showTabs: false,
                        type: 'coin',
                        callback: coin => {
                          setFromCurrency(coin.id)
                          setFromValue('')
                          // Also fetch available currencies for the selected From
                          fetchAvailableCurrencies(coin.id)
                          navigate(`/convert/${coin.id.toUpperCase()}_${toCurrency ? toCurrency.toUpperCase() : ''}`)
                        }
                      })
                    )
                    dispatch(setSelectCurrencyModalOpen(true))
                  }
                }}
                className={Styles.dropdownButton}
              >
                ▼
              </button>
            </div>
          </div>
          <div className={Styles.marketCard}>
            <div className={Styles.marketCardContent}>
              <div className={Styles.marketCardInfo}>
                <div className={Styles.marketCardHeader}>
                  <div className={Styles.marketCardIcon} style={getCurrencyIcon(fromCurrency).iconStyle}>
                    <img
                      src={currencies ? currencies?.find(el => el.id === fromCurrency?.toLowerCase())?.icon_url : ''}
                    />
                  </div>
                  <span className={Styles.marketCardName}>
                    {(currencies &&
                      currencies?.find(el => el.id === fromCurrency?.toLowerCase())?.name?.toUpperCase()) ||
                      fromCurrency?.toUpperCase()}
                  </span>
                </div>
                <div className={Styles.marketCardPrice}>
                  <span className={Styles.priceValue}>
                    ≈{' '}
                    {fromCurrency === 'USD' || fromCurrency === 'usd' || fromCurrency === 'GBP' || fromCurrency === 'gbp' || fromCurrency === 'EUR' || fromCurrency === 'eur' ? 1 : calculatedBaseCurrency || 'Calculating...'}
                  </span>
                  <span
                    className={`${Styles.priceChange} ${String(calculatedBasePercentageChange).includes('-') ? Styles.negative : Styles.positive
                      }`}
                  >
                    {calculatedBasePercentageChange || '+0.00%'}
                  </span>
                </div>
              </div>
              {/* Graph on the right side */}
              <div className={Styles.marketCardGraph}>
                <AreaChart kLineData={fromKlineArray?.map?.(subArr => subArr[2]) || []} theme={theme} />
              </div>
            </div>
          </div>

          {/* Additional Details in From Section */}
          <div className={Styles.walletInfo}>
            <div className={Styles.walletBalance}>
              <span className={Styles.walletLabel}>Wallet Balance</span>
              <span className={Styles.walletValue}>
                {balances?.find(el => el.currency === fromCurrency)?.balance ?? '0.00'} {fromCurrency?.toUpperCase()}
              </span>
            </div>

            {/* Fees and Maximum Amount Box */}
            {userData.email && fromMaxAmount && (
              <div className={Styles.feesContainer}>
                <div className={Styles.feeRow}>
                  <span className={Styles.feeLabel}>
                    <Info className={Styles.infoIcon} />
                    Fees incurred to Convert
                  </span>
                  <span className={Styles.feeValue}>
                    {calculateFee(fromValue || '0', fromConversionFees)} {fromCurrency?.toUpperCase()}
                  </span>
                </div>
                <div className={Styles.feeRow}>
                  <span className={Styles.feeLabel}>
                    <Info className={Styles.infoIcon} />
                    Maximum conversion Amount
                  </span>
                  <span className={Styles.feeValue}>
                    {formatNumber(
                      fromMaxAmount.maximum_amount ||
                      fromMaxAmount.max_amount ||
                      fromMaxAmount.maxAmount ||
                      fromMaxAmount.amount ||
                      '0.00'
                    )}{' '}
                    {fromCurrency?.toUpperCase()}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Swap Button */}
        <div className={Styles.swapButtonContainer}>
          <button onClick={handleReverse} className={Styles.swapButton}>
            <ArrowUpDown className={Styles.swapIcon} />
          </button>
        </div>

        {/* To Section */}
        <div className={Styles.inputSection}>
          <div className={Styles.sectionHeader}>
            <span className={Styles.sectionLabel}>To</span>
          </div>
          <div className={Styles.inputRow}>
            <input
              type='number'
              value={toValue || convertedPrice ? toValue || convertedPrice : ''}
              onChange={handleToValueChange}
              className={Styles.amountInput}
              placeholder='0.00'
            />
            <div className={Styles.currencySelector}>
              <div className={Styles.currencyIcon} style={getCurrencyIcon(toCurrency).iconStyle}>
                <img src={currencies ? currencies?.find(el => el.id === toCurrency?.toLowerCase())?.icon_url : ''} />
              </div>
              <span className={Styles.currencyText}>{toCurrency?.toUpperCase()}</span>
              <button
                onClick={() => {
                  if (!fromCurrency) {
                    showErrorSnackbar({
                      alertMessage: 'Please select a From currency first'
                    })
                    return
                  }

                  // Fetch currencies that can be converted TO from the selected From currency
                  _getAvailableCurrencies(
                    fromCurrency,
                    availableCurrencies => {
                      // Log the available currencies
                      console.log(
                        `For FROM=${fromCurrency}, can convert TO:`,
                        availableCurrencies.map(c => c.id.toUpperCase())
                      )

                      // Update the modal with available currencies
                      dispatch(
                        setSelectCurrencyModalData({
                          title: 'Select Currency',
                          showTabs: false,
                          searchPlaceholder: 'Search coin name',
                          type: 'coin',
                          currenciesToHide: [fromCurrency],
                          availableCurrencies,
                          callback: coin => {
                            setToCurrency(coin.id)
                            navigate(`/convert/${fromCurrency.toUpperCase()}_${coin.id.toUpperCase()}`)
                          }
                        })
                      )

                      // Open the modal
                      dispatch(setSelectCurrencyModalOpen(true))
                    },
                    err => {
                      console.error('Error fetching available currencies:', err)
                      showErrorSnackbar({
                        alertMessage: 'Error loading available currencies'
                      })
                    }
                  )
                }}
                className={Styles.dropdownButton}
              >
                ▼
              </button>
            </div>
          </div>
          <div className={Styles.marketCard}>
            <div className={Styles.marketCardContent}>
              <div className={Styles.marketCardInfo}>
                <div className={Styles.marketCardHeader}>
                  <div className={Styles.marketCardIcon} style={getCurrencyIcon(toCurrency).iconStyle}>
                    <img
                      src={currencies ? currencies?.find(el => el.id === toCurrency?.toLowerCase())?.icon_url : ''}
                    />
                  </div>
                  <span className={Styles.marketCardName}>
                    {(currencies && currencies?.find(el => el.id === toCurrency?.toLowerCase())?.name?.toUpperCase()) ||
                      toCurrency?.toUpperCase()}
                  </span>
                </div>
                <div className={Styles.marketCardPrice}>
                  <span className={Styles.priceValue}>
                    ≈ {toCurrency === 'USD' || toCurrency === 'usd' || toCurrency === 'GBP' || toCurrency === 'gbp' || toCurrency === 'EUR' || toCurrency === 'eur' ? 1 : calculatedQuoteCurrency || 'Calculating...'}
                  </span>
                  <span
                    className={`${Styles.priceChange} ${String(calculatedQuotePercentageChange).includes('-') ? Styles.negative : Styles.positive
                      }`}
                  >
                    {calculatedQuotePercentageChange || '+0.00%'}
                  </span>
                </div>
              </div>
              {/* Graph on the right side */}
              <div className={Styles.marketCardGraph}>
                <AreaChart kLineData={toKlineArray?.map?.(subArr => subArr[2]) || []} theme={theme} />
              </div>
            </div>
          </div>

          {/* Wallet Balance for To Section */}
          <div className={Styles.walletBalance}>
            <span className={Styles.walletLabel}>Wallet Balance</span>
            <span className={Styles.walletValue}>
              {balances?.find(el => el.currency === toCurrency)?.balance ?? '0.00'} {toCurrency?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Exchange Rate */}
        <div className={Styles.exchangeRate}>
          <ExchangeRate startText='Exchange rate: ' fromCurrencyId={fromCurrency} toCurrencyId={toCurrency} />
        </div>

        {/* Convert Button */}
        <button onClick={handleConvert} className={Styles.convertButton} disabled={isAmountExceedingMax}>
          Convert
        </button>

        {/* Disclaimer */}
        <div className={Styles.disclaimer}>The graph and price depicts the correlation between the coin and USD</div>
      </div>

      {/* Features */}
      <div className={Styles.iconsContainer}>
        {convertFeaturesContent.map(el => (
          <ConvertFeature key={el.title} title={el.title} icon={el.icon} />
        ))}
      </div>

      {/* FAQ */}
      <SectionHeader title='FAQ' />

      <Accordions
        containerStyle={{
          maxWidth: 700,
          margin: '0 auto',
          padding: '0 20px'
        }}
        accordions={questions}
      />

      {/* -------------- MODALS-------------- */}
      {confirmConvertModalOpen ? (
        <ConfirmConvertModal
          open={confirmConvertModalOpen}
          fromValue={fromValue}
          toValue={toValue}
          currencies={currencies}
          markets={markets}
          marketTickers={marketTickers}
          handleClose={() => setConfirmConvertModalOpen(false)}
          fromCurrency={currencies.length && currencies?.find(el => el.id === fromCurrency)}
          toCurrency={currencies.length && currencies?.find(el => el.id === toCurrency)}
        />
      ) : null}
      {successConvertModalOpen ? (
        <SuccessConvertModal
          open={successConvertModalOpen}
          fromValue={fromValue}
          toValue={toValue}
          currencies={currencies}
          markets={markets}
          marketTickers={marketTickers}
          handleClose={() => setSuccessConvertModalOpen(false)}
          fromCurrency={currencies.length && currencies?.find(el => el.id === fromCurrency)}
          toCurrency={currencies.length && currencies?.find(el => el.id === toCurrency)}
        />
      ) : null}
    </div>
  )
}

export default injectIntl(Convert)
